import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Shield } from 'lucide-react';

export default function Privacy() {
  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  return (
    <div className="privacy-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --accent-primary: #D97642;
          --border-primary: rgba(212, 196, 168, 0.15);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --accent-primary: #D97642;
          --border-primary: rgba(26, 22, 18, 0.15);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .privacy-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .privacy-content {
          flex: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          width: 100%;
        }

        .privacy-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .update-date {
          font-size: 0.9375rem;
          color: var(--text-secondary);
          margin-bottom: 3rem;
        }

        .section {
          margin-bottom: 2.5rem;
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.375rem;
          margin-bottom: 1rem;
        }

        .section-text {
          font-size: 1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }

        ul {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }

        li {
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 0.5rem;
        }

        a {
          color: var(--accent-primary);
        }

        @media (max-width: 768px) {
          .privacy-content {
            padding: 2rem 1.5rem;
          }

          .privacy-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.125rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="privacy-content">
        <h1 className="privacy-title">
          <Shield size={32} />
          Privacy Policy
        </h1>
        <p className="update-date">Last updated: May 7, 2026</p>

        <div className="section">
          <h2 className="section-title">Introduction</h2>
          <p className="section-text">
            MIZAR is operated by TravelTxter Ltd. This Privacy Policy explains how we collect, use, store, and protect information when you use the MIZAR website, console, API, or related services.
          </p>
          <p className="section-text">
            MIZAR is a B2B flight price prediction API. The service is designed to process route, fare, and timing information. It does not require passenger names, passport details, payment card information, or traveller identity data to generate a RegretRisk score.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Information We Collect</h2>
          <p className="section-text">We may collect the following categories of information:</p>
          <ul>
            <li><strong>Account information:</strong> name, email address, company name, role, billing details, and login information.</li>
            <li><strong>API usage information:</strong> API key usage, request timestamps, response metadata, model version, decision identifiers, and usage volumes.</li>
            <li><strong>Flight signal inputs:</strong> origin, destination, travel dates, trip type, cabin class, fare amount, and related route context submitted to the API.</li>
            <li><strong>Technical information:</strong> IP address, browser type, device information, logs, diagnostics, and security events.</li>
            <li><strong>Communications:</strong> messages sent through contact forms, email, support requests, or pilot discussions.</li>
          </ul>
        </div>

        <div className="section">
          <h2 className="section-title">Information MIZAR Does Not Require</h2>
          <p className="section-text">
            MIZAR does not require the following information to provide its core scoring service:
          </p>
          <ul>
            <li>Passenger names</li>
            <li>Passport or identity document details</li>
            <li>Payment card information</li>
            <li>Full booking records containing personal traveller identity</li>
            <li>Special category personal data</li>
          </ul>
          <p className="section-text">
            Clients should not send unnecessary personal information to the MIZAR API.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">How We Use Information</h2>
          <p className="section-text">We use information to:</p>
          <ul>
            <li>Provide and operate the MIZAR API and related services.</li>
            <li>Generate RegretRisk scores, recommendations, and confidence metadata.</li>
            <li>Monitor system performance, reliability, and security.</li>
            <li>Measure API usage for billing, rate limits, and operational reporting.</li>
            <li>Support pilots, onboarding, customer success, and technical troubleshooting.</li>
            <li>Improve model performance, validation processes, and product quality.</li>
            <li>Comply with legal, tax, accounting, and regulatory obligations.</li>
          </ul>
        </div>

        <div className="section">
          <h2 className="section-title">API Data and Shadow Mode</h2>
          <p className="section-text">
            During Shadow Mode, clients may call the MIZAR API alongside their existing booking flow without displaying customer-facing signals. MIZAR processes the submitted route and fare context and returns a score, recommendation, confidence metadata, and decision identifier.
          </p>
          <p className="section-text">
            Shadow Mode does not require MIZAR to modify pricing, checkout, payment, or customer session behaviour.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Data Sharing and Disclosure</h2>
          <p className="section-text">
            We do not sell personal information. We may share information where necessary with:
          </p>
          <ul>
            <li>Service providers that support hosting, infrastructure, analytics, billing, security, and communications.</li>
            <li>Professional advisers, auditors, accountants, insurers, or legal representatives.</li>
            <li>Authorities, regulators, or courts where legally required.</li>
            <li>Parties involved in a business transfer, merger, acquisition, restructuring, or sale of assets.</li>
            <li>Other parties where you have provided consent or instructed us to do so.</li>
          </ul>
        </div>

        <div className="section">
          <h2 className="section-title">Data Security</h2>
          <p className="section-text">
            We use reasonable technical and organisational measures to protect information against unauthorised access, alteration, disclosure, or destruction. These measures may include access controls, HTTPS transmission, authentication controls, logging, monitoring, and restricted administrative access.
          </p>
          <p className="section-text">
            No system is completely secure. Clients are responsible for protecting their API keys and ensuring that only authorised systems and personnel can access them.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Data Retention</h2>
          <p className="section-text">
            We retain information for as long as necessary to provide the service, operate the API, support billing and audit requirements, maintain security, validate model performance, and comply with legal obligations.
          </p>
          <p className="section-text">
            Where information is no longer required, we will delete it or anonymise it where reasonably practicable.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Cookies and Website Analytics</h2>
          <p className="section-text">
            We may use cookies and similar technologies to operate the website, remember preferences, analyse usage, improve performance, and support security. You can control cookies through your browser settings.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Your Rights</h2>
          <p className="section-text">
            Depending on your location and applicable law, you may have rights to access, correct, delete, restrict, object to, or export personal information held about you.
          </p>
          <p className="section-text">
            To exercise these rights, contact us using the details below. We may need to verify your identity before responding.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">International Transfers</h2>
          <p className="section-text">
            We may use service providers located outside your country. Where required, we take appropriate steps to protect information transferred internationally in accordance with applicable data protection laws.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Changes to This Policy</h2>
          <p className="section-text">
            We may update this Privacy Policy from time to time. The updated version will be posted on this page with a revised “Last updated” date.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Contact Us</h2>
          <p className="section-text">
            If you have questions about this Privacy Policy, contact us at:{' '}
            <a href="mailto:richard.king@traveltxter.com">richard.king@traveltxter.com</a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}